import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Tooltip, Button, Alert, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Divider, Switch,
  FormControlLabel, Grid
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, Settings, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const GENDER_COLOR = { All: 'default', Male: 'primary', Female: 'secondary' };

const emptyForm = {
  code: '', name: '', daysPerYear: '', isPaid: true, carryForward: false,
  maxCarryForwardDays: 0, gender: 'All', minServiceMonths: 0,
  maxDaysPerApplication: 0, requiresReliever: false, description: '',
};

const LeaveTypeList = () => {
  const navigate = useNavigate();
  const [types,   setTypes]   = useState([]);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open, value: form, onClose: () => setOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [formErr,    setFormErr]    = useState('');
  const [confirmDlg, setConfirmDlg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/leave/types/all`, { headers: hdrs() });
      setTypes(data);
    } catch { setError('Failed to load leave types'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, isActive: true });
    setFormErr(''); setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      code: t.code, name: t.name,
      daysPerYear: t.daysPerYear !== null && t.daysPerYear !== undefined ? t.daysPerYear : '',
      isPaid: t.isPaid !== false, carryForward: t.carryForward === true,
      maxCarryForwardDays: t.maxCarryForwardDays || 0, gender: t.gender || 'All',
      minServiceMonths: t.minServiceMonths || 0, maxDaysPerApplication: t.maxDaysPerApplication || 0,
      requiresReliever: t.requiresReliever === true, description: t.description || '',
      isActive: t.isActive !== false,
    });
    setFormErr(''); setOpen(true);
  };

  const handle = (e) => {
    const { name, value, checked, type } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.code?.trim()) { setFormErr('Code is required'); return; }
    if (!form.name?.trim()) { setFormErr('Name is required'); return; }
    setSaving(true); setFormErr('');
    try {
      const body = {
        ...form,
        daysPerYear: form.daysPerYear === '' ? null : Number(form.daysPerYear),
        maxCarryForwardDays:   Number(form.maxCarryForwardDays),
        minServiceMonths:      Number(form.minServiceMonths),
        maxDaysPerApplication: Number(form.maxDaysPerApplication),
      };
      if (editing) {
        await axios.put(`${API}/api/leave/types/${editing.code}`, body, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/leave/types`, body, { headers: hdrs() });
      }
      setOpen(false);
      setSuccess(editing ? 'Updated' : 'Created');
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) { setFormErr(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const confirmAction = async () => {
    const t = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/leave/types/${t.code}`, { headers: hdrs() });
      setSuccess('Deleted'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/hr/leave')}>Back</Button>
              <Settings sx={{ color: '#1565c0' }} />
              <Typography variant="h5" fontWeight={700} color="#1565c0">Leave Types</Typography>
              <Chip label={`${types.filter(t => t.isActive !== false).length} active`} size="small" color="success" />
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}
              sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
              Add Leave Type
            </Button>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell align="center"><strong>Days/Year</strong></TableCell>
                <TableCell align="center"><strong>Paid</strong></TableCell>
                <TableCell align="center"><strong>Carry Fwd</strong></TableCell>
                <TableCell align="center"><strong>Gender</strong></TableCell>
                <TableCell align="center"><strong>Min Service</strong></TableCell>
                <TableCell align="center"><strong>Max / App</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No leave types found. Defaults are seeded on first access.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : types.map(t => (
                <TableRow key={t.code} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Chip label={t.code} size="small" variant="outlined"
                      sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}
                      color={t.isActive === false ? 'text.disabled' : 'inherit'}>
                      {t.name}
                    </Typography>
                    {t.description && (
                      <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {t.daysPerYear === null || t.daysPerYear === undefined
                      ? <Chip label="Unlimited" size="small" color="warning" variant="outlined" />
                      : <Typography variant="body2" fontWeight={600}>{t.daysPerYear}</Typography>
                    }
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={t.isPaid !== false ? 'Paid' : 'Unpaid'} size="small"
                      color={t.isPaid !== false ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    {t.carryForward
                      ? <Chip label={t.maxCarryForwardDays > 0 ? `Max ${t.maxCarryForwardDays}` : 'Yes'} size="small" color="info" variant="outlined" />
                      : <Typography variant="caption" color="text.disabled">No</Typography>
                    }
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={t.gender || 'All'} size="small"
                      color={GENDER_COLOR[t.gender || 'All']} variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    {t.minServiceMonths > 0
                      ? <Typography variant="caption">{t.minServiceMonths} mo</Typography>
                      : <Typography variant="caption" color="text.disabled">—</Typography>
                    }
                  </TableCell>
                  <TableCell align="center">
                    {t.maxDaysPerApplication > 0
                      ? <Typography variant="body2" fontWeight={600}>{t.maxDaysPerApplication}</Typography>
                      : <Typography variant="caption" color="text.disabled">—</Typography>
                    }
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={t.isActive !== false ? 'Active' : 'Inactive'} size="small"
                      color={t.isActive !== false ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(t)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setConfirmDlg(t)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Confirm Delete Dialog */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Leave Type
        </DialogTitle>
        <DialogContent>
          <Typography>
            Delete leave type <strong>{confirmDlg?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog ── */}
      {discardDialog}
      <Dialog open={open} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.name}` : 'Add Leave Type'}</DialogTitle>
        <DialogContent dividers>
          {formErr && <Alert severity="error" sx={{ mb: 2 }}>{formErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Code *" name="code" value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                size="small" fullWidth disabled={!!editing}
                helperText={editing ? 'Cannot change' : 'e.g. AL, SL, ML'}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Name *" name="name" value={form.name}
                onChange={handle} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>

            <Grid item xs={4}>
              <TextField label="Days Per Year" name="daysPerYear" value={form.daysPerYear}
                onChange={handle} size="small" fullWidth type="number"
                helperText="Leave blank = unlimited" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={4}>
              <TextField select label="Gender" name="gender" value={form.gender}
                onChange={handle} size="small" fullWidth InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}>
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Male">Male only</MenuItem>
                <MenuItem value="Female">Female only</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField label="Min Service (months)" name="minServiceMonths"
                value={form.minServiceMonths} onChange={handle}
                size="small" fullWidth type="number" InputLabelProps={{ shrink: true }}
                helperText="0 = no minimum" />
            </Grid>

            <Grid item xs={6}>
              <TextField label="Max Days / Application" name="maxDaysPerApplication"
                value={form.maxDaysPerApplication} onChange={handle}
                size="small" fullWidth type="number" InputLabelProps={{ shrink: true }}
                helperText="0 = no cap per application" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Max Carry Forward Days" name="maxCarryForwardDays"
                value={form.maxCarryForwardDays} onChange={handle}
                size="small" fullWidth type="number" InputLabelProps={{ shrink: true }}
                helperText="0 = no cap (if carry forward enabled)" />
            </Grid>

            <Grid item xs={12}>
              <TextField label="Description" name="description" value={form.description}
                onChange={handle} size="small" fullWidth multiline rows={2}
                InputLabelProps={{ shrink: true }} />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={3} flexWrap="wrap">
                <FormControlLabel
                  control={<Switch checked={form.isPaid} name="isPaid"
                    onChange={e => setForm(f => ({ ...f, isPaid: e.target.checked }))} />}
                  label="Paid Leave" />
                <FormControlLabel
                  control={<Switch checked={form.carryForward} name="carryForward"
                    onChange={e => setForm(f => ({ ...f, carryForward: e.target.checked }))} />}
                  label="Allow Carry Forward" />
                <FormControlLabel
                  control={<Switch checked={form.requiresReliever} name="requiresReliever"
                    onChange={e => setForm(f => ({ ...f, requiresReliever: e.target.checked }))} />}
                  label="Requires Reliever" />
                {editing && (
                  <FormControlLabel
                    control={<Switch checked={form.isActive !== false}
                      onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                    label="Active" />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveTypeList;
